import React from 'react';
import { Tabs, TabList, Tab, TabPanels, TabPanel } from '@reach/tabs';
import { isEmpty } from 'lodash';
import styled from 'styled-components';
import he from 'he';

import { getFullUrl } from '@src/helpers/url';
import { Avatar, RenderAsHTMLStyles } from '@src/features/common';
import { Flex, TextBox, Text } from 'src/kit';
import {
  CompanyBaseCard,
  CompanyBaseCover,
  VideoCard,
  InfluincerCard,
  MediaCard,
  ActivitiesCard
} from '../atoms/MagazineCards';
import { convertVideoLink } from './convertVideoLinkToEmbedded';

const ClampedHTML = styled(RenderAsHTMLStyles)<{ lines: number }>`
  display: -webkit-box;
  -webkit-line-clamp: ${({ lines }) => lines};
  -webkit-box-orient: vertical;
  overflow: hidden;

  /* Fallback in case line-clamp fails (non-WebKit) */
  max-height: ${({ lines }) => `${lines * 1.57}em`};
  line-height: 1.57;
`;

export const renderMagazines = ({
  published_jobs_present,
  media,
  activities,
  influencer_name,
  influencer_avatar_url,
  influencer_position,
  cover_picture_url,
  influencer_bio,
  logo_url,
  name,
  slug,
  industry,
  desc_text
}: IMagazine) => {
  const href = getFullUrl(slug);

  const renderMainCompanyCard = () => {
    return (
      <CompanyBaseCard
        href={href}
        css={`
          @media (max-width: 1024px) {
            margin-left: auto;
            margin-right: auto;
          }
        `}
      >
        <CompanyBaseCover src={cover_picture_url}>
          <img className="logo" src={logo_url} alt="cover"></img>
        </CompanyBaseCover>
        <Flex flexDirection="column" className="card-content">
          <TextBox
            className="name"
            color="textPrimary"
            fontSize="20px"
            height="20px"
            lineHeight="20px"
            textStyle="ellipsis"
          >
            {name}
          </TextBox>
          <TextBox color="blueGreyTwo" fontSize="14px" mt={15} mb={20}>
            {industry}
          </TextBox>
          <TextBox color="textSecondary" lineHeight="1.57" fontSize="14px" height="110px">
            <ClampedHTML lines={5} dangerouslySetInnerHTML={{ __html: he.decode(desc_text) }} />
          </TextBox>
        </Flex>
      </CompanyBaseCard>
    );
  };
  const renderInfluincerCard = () => {
    return (
      <InfluincerCard
        href={href}
        css={`
          @media (max-width: 1024px) {
            margin-left: auto;
            margin-right: auto;
          }
        `}
      >
        <Avatar
          src={influencer_avatar_url}
          size={130}
          firstName={influencer_name}
          bg="transparent"
          objectFit="cover"
          mx="auto"
        ></Avatar>
        <Flex flexDirection="column">
          <TextBox textAlign="center" mb={15} mt={20} fontSize={20} color="textPrimary">
            {influencer_name}
          </TextBox>
          <TextBox textAlign="center" mb={20} fontSize={14} color="blueGreyTwo">
            {influencer_position}
          </TextBox>
          <TextBox textAlign="center" height="72px" lineHeight="1.57" fontSize={14} color="textSecondary">
            <ClampedHTML lines={3} dangerouslySetInnerHTML={{ __html: he.decode(influencer_bio) }} />
          </TextBox>
        </Flex>
      </InfluincerCard>
    );
  };

  const renderActivitesCard = () => (
    <ActivitiesCard href={href}>
      <Text color="textPrimary" fontSize="20px" lineHeight="20px" textStyle="ellipsis" whiteSpace="normal">
        {name}: Activities
      </Text>
      <ResponsiveTabs>
        <TabListWrapper>
          <ResponsiveTabList>
            {activities.map(a => (
              <ResponsiveTab key={a.id}>
                <Text color="textSecondary" fontSize="16px" textStyle="nowrap">
                  {a.name}
                </Text>
              </ResponsiveTab>
            ))}
          </ResponsiveTabList>
        </TabListWrapper>
        <TabPanels>
          {activities.map(a => (
            <ResponsiveTabPanel key={a.id}>
              <ClampedHTML lines={6} dangerouslySetInnerHTML={{ __html: he.decode(a.desc_text_html) }} />
            </ResponsiveTabPanel>
          ))}
        </TabPanels>
      </ResponsiveTabs>
    </ActivitiesCard>
  );

  const renderWithMediaCard = ({
    src = null,
    as = 'img',
    width = 'calc(33% - 28px)'
  }: {
    src?: string | null;
    as?: 'img' | 'video';
    width?: string;
  }) => {
    const Wrapper = as === 'video' ? VideoCard : MediaCard;
    return (
      <Wrapper flex={`1 1 ${width}`} src={src || cover_picture_url} href={href}>
        <Flex
          className="wrapper"
          css={`          @media (max-width: 1024px) {
            margin-left: auto;
            margin-right: auto;`}
        >
          {false && <img className="logo" src={logo_url} alt="logo" />}
          <Flex flexDirection="column" ml={12}>
            <TextBox mb={10} fontSize={20} color="white" textStyle="firstLetter">
              {name}
            </TextBox>
            <Text fontSize={14} color="white" textStyle="firstLetter">
              {industry}
            </Text>
          </Flex>
        </Flex>
      </Wrapper>
    );
  };

  if (published_jobs_present) {
    return (
      <>
        {renderMainCompanyCard()}
        {typeof media !== 'boolean' && media?.image && renderWithMediaCard({ src: media.image })}
        {typeof media !== 'boolean' &&
          media?.video &&
          renderWithMediaCard({ src: convertVideoLink(media.video), as: 'video' })}
        {!isEmpty(influencer_name) && renderInfluincerCard()}
        {!isEmpty(activities) && renderActivitesCard()}
      </>
    );
  } else {
    return renderWithMediaCard({ width: 'calc(50% - 28px)' });
  }
};

export interface IMagazine {
  id: number;
  slug: string;
  name: string;
  cover_picture_url: string;
  logo_url: string;
  industry: string;
  desc_text: string;
  influencer_avatar_url: null;
  influencer_name: string;
  influencer_position: string;
  influencer_bio: string;
  media:
    | {
        image: string;
        video: string;
      }
    | boolean;
  activities: Array<{ id: number; name: string; desc_text_html: string }>;
  published_jobs_present: boolean;
}

const ResponsiveTabs = styled(Tabs)`
  width: 100%;
  min-width: 0;
  overflow: hidden;
`;

const TabListWrapper = styled.div`
  width: 100%;
  overflow: hidden;
  min-width: 0;
`;

const ResponsiveTabList = styled(TabList)`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: thin;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 8px;
  min-width: 0;

  /* Force le conteneur à ne pas dépasser */
  max-width: 100%;

  &::-webkit-scrollbar {
    height: 4px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const ResponsiveTab = styled(Tab)`
  flex: 0 0 auto;
  flex-shrink: 0;
  min-width: 0;
  max-width: 200px;
  padding: 8px 16px;
  white-space: nowrap;

  @media (max-width: 576px) {
    padding: 6px 12px;
    max-width: 150px;
  }
`;

const ResponsiveTabPanel = styled(TabPanel)`
  width: 100%;
  min-width: 0;
  overflow: hidden;
  word-wrap: break-word;
  overflow-wrap: break-word;
`;
